import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react'
import type { TProject, TProjectFeatureName } from 'src/types/project'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import * as thisModule from './current-project.provider'
import type { TUser } from 'src/types/user'
import type { TLocation } from 'src/types/others'
import { ProjectService } from 'src/services/project.service'
import { useQuery } from '@tanstack/react-query'
import type { TMedicalStaffMember } from 'src/types/medical-staff-member'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
  EPermissionSpecialAuthorization,
  TPermission,
  TPermissionGrant,
} from 'src/pages/configuration/permissions/_components/permissions.type'
import type { TMedEvent } from 'src/types/med-event'
import { useTranslation } from 'react-i18next'
import { TFollowup } from 'src/types/followup'
import { useSession } from './auth.provider'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { InsufficientPermission } from 'src/pages/configuration/permissions/_components/insufficient-permission.component'
import { TPlanningTab, TSearchPlanningTab } from 'src/pages/medical/planning/_components/planning.type'
import { buildAllPlanningTab } from 'src/pages/medical/planning/_components/planning-all-tab.util'

const getTranslatedLabel = (locale: string, intlName: Record<string, string> | null | undefined) => {
  if (!intlName) return ''
  return intlName[locale] || intlName['fr'] || intlName['en'] || ''
}

type TCurrentProjectContext = {
  project: TProject
  planningTabs: TPlanningTab[]
  allPlanningTab: TSearchPlanningTab
  isFeatureActivated: (featureName: TProjectFeatureName) => boolean
  isAtLeastOneFeatureActivated: (featureNames: TProjectFeatureName[]) => boolean
  followups: TFollowup[]
  getFollowupById: (id: number) => TFollowup | undefined
  availableMedEventOptions: Array<{ value: number; label: string }>
  medEvents: TMedEvent[]
  getMedEventLabelById: (medEventId?: number) => string
  users: TUser[]
  activeDoctors: TMedicalStaffMember[]
  currentPermission: TPermission
  hasSpecialAuthorization: (requiredSpecialAuthorization: EPermissionSpecialAuthorization) => boolean
  hasPermission: (
    requiredSubject: EPermissionGrantSubjects,
    requiredAction: EPermissionGrantActions,
  ) => boolean
  hasAnyPermission: (
    requiredPermissions?: { subject: EPermissionGrantSubjects; action: EPermissionGrantActions }[],
  ) => boolean
  locations: TLocation[]
  updateMedicalStaffMembers: (medicalStaffMember: TMedicalStaffMember) => void
}
const CurrentProjectContext = createContext<TCurrentProjectContext>({} as TCurrentProjectContext)
export const useCurrentProject = () => useContext(CurrentProjectContext)

const CurrentProjectProvider = ({
  children,
  project,
  isProjectLoading,
  projectError,
}: {
  children: React.ReactNode
  project?: TProject
  isProjectLoading: boolean
  projectError: unknown
}) => {
  const { currentUser } = useSession()
  const { i18n } = useTranslation()
  const locale = i18n.language || 'fr'

  // PROJECT FEATURES
  const isFeatureActivated = (featureName: TProjectFeatureName) => {
    return !!project?.settings?.features?.includes(featureName)
  }
  const isAtLeastOneFeatureActivated = (featureNames: TProjectFeatureName[]) => {
    if (!featureNames || featureNames.length === 0) return true
    return featureNames.some(isFeatureActivated)
  }

  // PROJECT MED EVENTS
  const medEvents: TMedEvent[] = useMemo(
    () =>
      project?.MedEvents?.map((medEvent) => ({
        id: medEvent.id,
        intlName: medEvent.intlName,
        type: medEvent.type,
      })) || [],
    [project?.MedEvents],
  )
  const getMedEventLabelById = useCallback(
    (medEventId?: number) => {
      if (!medEventId) return ''
      const medEvent = medEvents.find((me) => me.id === medEventId)
      return getTranslatedLabel(locale, medEvent?.intlName)
    },
    [medEvents, locale],
  )

  // PROJECT FOLLOWUPS
  const followups: TFollowup[] = useMemo(
    () =>
      project?.Followups?.map((followup: any) => ({
        id: followup.id,
        ProjectId: followup.ProjectId,
        name: followup.name || '',
        description: followup.description || '',
        type: followup.type,
        configUI: followup.configUI,
        idleDaysBeforeFollowupEnd: followup.idleDaysBeforeFollowupEnd,
        position: followup.position,
      })).sort((a, b) => (a.position < b.position ? -1 : 1)) || [],
    [project?.Followups],
  )
  const getFollowupById = (followupId: number) => {
    return followups?.find((followup) => followup.id === followupId)
  }
  const availableMedEventOptions = useMemo(() => {
    const medEvents = followups
      .map((followup) =>
        followup.configUI?.medEventsUI?.map((medEventUI) => ({
          value: medEventUI.MedEvent.id,
          label: getMedEventLabelById(medEventUI.MedEvent.id),
        })),
      )
      .filter((medEvents) => !!medEvents)
      .flat()

    // remove dupplicates and sort by label
    const uniqMedEvents = medEvents
      .filter((option, index, self) => index === self.findIndex((opt) => opt.value === option.value))
      .sort((medevent1, medevent2) =>
        medevent1.label.localeCompare(medevent2.label, locale, { sensitivity: 'base' }),
      )
    return uniqMedEvents
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followups])

  // USERS
  const updateMedicalStaffMembers = (medicalStaffMember: TMedicalStaffMember) => {
    if (project?.MedicalStaffMembers) {
      project.MedicalStaffMembers = [
        ...project.MedicalStaffMembers.filter((item) => item.id !== medicalStaffMember.id),
        medicalStaffMember,
      ]
    }
  }
  // RPPS professionalCode 10 = Médecin — filters out paramedical staff
  const activeDoctors = useMemo(
    () =>
      project?.MedicalStaffMembers?.filter(
        (medicalStaffMember: TMedicalStaffMember) =>
          medicalStaffMember.professionalCode === '10' && medicalStaffMember.isActive,
      ) || [],
    [project?.MedicalStaffMembers],
  )
  const users: TUser[] = useMemo(() => project?.Users || [], [project?.Users])

  // PERMISSIONS
  const currentPermission = (project?.UserProjects?.[0]?.Permission || {}) as TPermission
  const hasPermission = (
    requiredSubject: EPermissionGrantSubjects,
    requiredAction: EPermissionGrantActions,
  ) => {
    if (currentUser?.role === 'admin') return true
    const userGrants = currentPermission?.grants || []
    const hasPermission = userGrants.some(
      (grant: TPermissionGrant) =>
        (grant.subject === EPermissionGrantSubjects.ALL || grant.subject === requiredSubject) &&
        (grant.actions?.includes(EPermissionGrantActions.ALL) || grant.actions?.includes(requiredAction)),
    )
    return hasPermission
  }
  const hasAnyPermission = (
    requiredPermissions?: { subject: EPermissionGrantSubjects; action: EPermissionGrantActions }[],
  ) => {
    if (requiredPermissions && requiredPermissions?.length > 0) {
      return requiredPermissions.some((perm) => hasPermission(perm.subject, perm.action))
    }
    return true
  }
  const hasSpecialAuthorization = (requiredSpecialAuthorization: EPermissionSpecialAuthorization) => {
    if (
      currentUser?.role === 'admin' &&
      requiredSpecialAuthorization !== EPermissionSpecialAuthorization.CAN_VIEW_SENSITIVE_DATA
    ) {
      return true
    }
    const userSpecialAuthorizations = currentPermission?.specialAuthorizations || []
    return userSpecialAuthorizations.includes(requiredSpecialAuthorization)
  }

  // LOCATIONS
  const {
    isLoading: isLoadingLocations,
    error: errorLocations,
    data: locations = [],
  } = useQuery<TLocation[]>({
    queryKey: ['locations', project?.id],
    queryFn: () => ProjectService.getProjectLocations(project?.id as number),
    enabled: !!project?.id,
  })

  const value: TCurrentProjectContext = useMemo(
    () => ({
      project: project as TProject,
      planningTabs:
        project?.PlanningTabs?.filter((tab) => !tab.isAllTab).sort(
          (tabA, tabB) => tabA.tabPosition - tabB.tabPosition,
        ) || [],
      allPlanningTab: buildAllPlanningTab(project?.PlanningTabs?.find((tab) => tab.isAllTab)),
      isFeatureActivated,
      isAtLeastOneFeatureActivated,
      followups: followups,
      getFollowupById: getFollowupById,
      availableMedEventOptions,
      medEvents,
      getMedEventLabelById,
      users,
      activeDoctors,
      currentPermission,
      hasPermission,
      hasSpecialAuthorization,
      hasAnyPermission,
      locations,
      updateMedicalStaffMembers,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project, project?.Followups, locations, project?.MedicalStaffMembers, activeDoctors],
  )

  if (projectError || errorLocations) return <ErrorQueryHandler error={projectError || errorLocations} />
  if (isProjectLoading || isLoadingLocations) return <div className="loading" />

  return <CurrentProjectContext.Provider value={value}>{children}</CurrentProjectContext.Provider>
}

export const WithFeatureEnabled = ({
  featuresAccepted,
  children,
  onFailure,
}: {
  featuresAccepted: TProjectFeatureName[]
  children: ReactNode
  onFailure?: 'redirect'
}) => {
  const navigate = useLocalizedNavigate()
  const { isAtLeastOneFeatureActivated, project } = thisModule.useCurrentProject()

  if (!isAtLeastOneFeatureActivated(featuresAccepted)) {
    if (onFailure === 'redirect') {
      navigate(`/${project.id}/`)
    }
    return null
  }

  return <>{children}</>
}

export const WithPermission = ({
  requiredSubject,
  requiredAction,
  children,
  onFailure,
}: {
  requiredSubject?: EPermissionGrantSubjects
  requiredAction?: EPermissionGrantActions
  children: ReactNode
  onFailure?: 'redirect' | 'permission-error-component'
}) => {
  const navigate = useLocalizedNavigate()
  const { hasPermission, project } = thisModule.useCurrentProject()

  if (requiredSubject && requiredAction && !hasPermission(requiredSubject, requiredAction)) {
    if (onFailure === 'redirect') {
      navigate(`/${project.id}/`)
    }
    if (onFailure === 'permission-error-component') {
      return <InsufficientPermission subject={requiredSubject} action={requiredAction} />
    }
    return null
  }

  return <>{children}</>
}

export const WithSpecialAuthorization = ({
  requiredSpecialAuthorization,
  children,
  onFailure,
}: {
  requiredSpecialAuthorization: EPermissionSpecialAuthorization | null
  children: ReactNode
  onFailure?: 'redirect'
}) => {
  const navigate = useLocalizedNavigate()
  const { hasSpecialAuthorization, project } = thisModule.useCurrentProject()

  if (requiredSpecialAuthorization && !hasSpecialAuthorization(requiredSpecialAuthorization)) {
    if (onFailure === 'redirect') {
      navigate(`/${project.id}/`)
    }
    return null
  }

  return <>{children}</>
}

export const WithoutFeatureEnabled = ({
  featuresRejected,
  children,
  onFailure,
}: {
  featuresRejected: TProjectFeatureName[]
  children: ReactNode
  onFailure?: 'redirect'
}) => {
  const navigate = useLocalizedNavigate()
  const { isAtLeastOneFeatureActivated, project } = thisModule.useCurrentProject()
  const { currentUser } = useSession()

  if (isAtLeastOneFeatureActivated(featuresRejected) && currentUser?.role !== 'admin') {
    if (onFailure === 'redirect') {
      navigate(`/${project.id}/`)
    }
    return null
  }

  return <>{children}</>
}

export default CurrentProjectProvider
